/**
 * The sign-in gate: everything an anonymous visitor sees.
 *
 * The screen is ported from the le-portal-ui-v3 prototype's `welcome` step
 * (src/LoginFlow.tsx) so the shipped portal matches the approved design — the shield
 * heading, the legal copy, the "How sign-in works" sequence, the eligibility checkbox and
 * the single primary action. Styling is the prototype's, on Fluent tokens.
 *
 * The prototype mocks sign-in with its own account/password steps. Here the button hands
 * off to Entra through MSAL instead, against the tenanted TESTTRS authority.
 *
 * Only Microsoft sign-in happens here. The prototype's later steps — confirming an official
 * agency email and the verification code — are the government-identity flow and live in
 * ../GovIdVerification, which the shell shows once sign-in succeeds.
 */
import { useState } from 'react';
import {
  Badge,
  Button,
  Card,
  Checkbox,
  makeStyles,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Subtitle1,
  Text,
  Title2,
  shorthands,
  tokens,
  Tooltip,
} from '@fluentui/react-components';
import { QuestionCircleRegular, ShieldLockRegular } from '@fluentui/react-icons';
import { useAuth } from '../../hooks/useAuth';
import { LoginShell } from '../LoginShell';
import { LOGIN_NARROW_MEDIA, useLoginStyles } from '../loginStyles';
import { LoginHelpDialog } from './LoginHelpDialog';

const useStyles = makeStyles({
  cardHeading: {
    display: 'grid',
    gridTemplateColumns: '48px minmax(0, 1fr)',
    ...shorthands.gap(tokens.spacingHorizontalL),
    alignItems: 'start',
    '& h1': {
      ...shorthands.margin(0, 0, tokens.spacingVerticalS),
    },
    '& p': {
      ...shorthands.margin(0),
      color: tokens.colorNeutralForeground3,
      lineHeight: 1.5,
    },
    [LOGIN_NARROW_MEDIA]: {
      gridTemplateColumns: '40px minmax(0, 1fr)',
    },
  },

  legalCopy: {
    paddingTop: `clamp(${tokens.spacingVerticalS}, 1.8vh, ${tokens.spacingVerticalXL})`,
    paddingBottom: `clamp(${tokens.spacingVerticalS}, 1.8vh, ${tokens.spacingVerticalXL})`,
    ...shorthands.borderTop(tokens.strokeWidthThin, 'solid', tokens.colorNeutralStroke2),
    ...shorthands.borderBottom(tokens.strokeWidthThin, 'solid', tokens.colorNeutralStroke2),
    '& p': {
      ...shorthands.margin(0),
      lineHeight: 1.55,
    },
  },

  guidance: {
    display: 'grid',
    ...shorthands.gap(`clamp(${tokens.spacingVerticalS}, 1.2vh, ${tokens.spacingVerticalM})`),
    '& h2': {
      ...shorthands.margin(0),
    },
    '& > span': {
      ...shorthands.margin(0),
      color: tokens.colorNeutralForeground3,
      lineHeight: 1.45,
    },
  },

  guidanceHeading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    ...shorthands.gap(tokens.spacingHorizontalM),
  },

  identitySequence: {
    display: 'grid',
    ...shorthands.padding(0),
    ...shorthands.margin(0),
    listStyleType: 'none',
    '& li': {
      display: 'grid',
      gridTemplateColumns: 'auto minmax(0, 1fr)',
      ...shorthands.gap(tokens.spacingHorizontalM),
      alignItems: 'start',
      paddingTop: `clamp(${tokens.spacingVerticalS}, 1.4vh, ${tokens.spacingVerticalL})`,
      paddingBottom: `clamp(${tokens.spacingVerticalS}, 1.4vh, ${tokens.spacingVerticalL})`,
    },
    '& li > div': {
      display: 'grid',
      ...shorthands.gap(tokens.spacingVerticalXS),
    },
    '& p': {
      ...shorthands.margin(0),
      color: tokens.colorNeutralForeground3,
      lineHeight: 1.45,
    },
  },
});

export function SignInPage() {
  const { isLoading, tenantBlocked, authError, login, logout } = useAuth();
  const [authorized, setAuthorized] = useState(false);
  const [loginHelpOpen, setLoginHelpOpen] = useState(false);
  const loginStyles = useLoginStyles();
  const styles = useStyles();

  return (
    <LoginShell>
      <Card appearance="outline" className={loginStyles.card}>
        <div className={styles.cardHeading}>
          <span className={loginStyles.headingIcon} aria-hidden="true">
            <ShieldLockRegular />
          </span>
          <div>
            <Title2 as="h1" id="login-content-title" tabIndex={-1}>
              Welcome to the Microsoft Law Enforcement Portal
            </Title2>
            <p>Secure access for authorized law enforcement partners.</p>
          </div>
        </div>

        {/* `layout="multiline"` is not cosmetic: the default single-line layout gives the
            bar a min-content width wide enough to hold the whole sentence, which overflows
            the card and clips every line in it, not just this one. */}
        {tenantBlocked && (
          <MessageBar intent="error" layout="multiline">
            <MessageBarBody>
              <MessageBarTitle>That account cannot use this portal.</MessageBarTitle>
              You signed in with an account this portal does not accept. Sign out, then
              sign in again with the account issued to you for it.
            </MessageBarBody>
            <Button appearance="transparent" onClick={() => void logout()}>
              Sign out
            </Button>
          </MessageBar>
        )}

        {/* A rejected account is the more specific condition, so it wins: showing both
            would leave the user guessing which one to act on. */}
        {!tenantBlocked && authError && (
          <MessageBar intent="error" layout="multiline">
            <MessageBarBody>
              <MessageBarTitle>Sign-in didn't complete.</MessageBarTitle>
              {authError}
            </MessageBarBody>
          </MessageBar>
        )}

        <div className={styles.legalCopy}>
          <p>
            This portal is only for authorized law enforcement officials submitting lawful
            requests connected with an official investigation. Microsoft evaluates each
            request individually under applicable law, customer commitments, and corporate
            policies.
          </p>
        </div>

        <section className={styles.guidance} aria-labelledby="before-you-continue-title">
          <div className={styles.guidanceHeading}>
            <Subtitle1 as="h2" id="before-you-continue-title">
              How sign-in works
            </Subtitle1>
            <Tooltip content="Sign-in help" relationship="label">
              <Button
                appearance="subtle"
                size="small"
                shape="circular"
                icon={<QuestionCircleRegular />}
                aria-label="Open sign-in help"
                onClick={() => setLoginHelpOpen(true)}
              />
            </Tooltip>
          </div>
          <Text>
            Sign-in is handled by Microsoft. You will be taken to the Microsoft sign-in
            page and returned here once you are signed in.
          </Text>
          <ol className={styles.identitySequence}>
            <li>
              <Badge
                appearance="tint"
                color="informative"
                shape="circular"
                size="medium"
                aria-hidden="true"
              >
                1
              </Badge>
              <div>
                <strong>First, sign in with Microsoft</strong>
                <p>
                  Use the Microsoft account you access this portal with. Sign-in is handled
                  by Microsoft, and an account this portal does not accept is turned away
                  there.
                </p>
              </div>
            </li>
            <li>
              <Badge
                appearance="tint"
                color="informative"
                shape="circular"
                size="medium"
                aria-hidden="true"
              >
                2
              </Badge>
              <div>
                <strong>Then, continue to the portal</strong>
                <p>
                  Once you are signed in you can submit a new request and track the ones
                  you have already filed.
                </p>
              </div>
            </li>
          </ol>
        </section>

        <Checkbox
          checked={authorized}
          onChange={(_, data) => setAuthorized(data.checked === true)}
          label="I confirm that I am an authorized law enforcement official or government employee making an official request."
        />

        <div className={loginStyles.actions}>
          <Button
            appearance="primary"
            disabled={!authorized || isLoading}
            onClick={() => void login()}
          >
            Sign in with Microsoft
          </Button>
        </div>

        <LoginHelpDialog open={loginHelpOpen} onOpenChange={setLoginHelpOpen} />
      </Card>
    </LoginShell>
  );
}
